/**
 * Palette de recherche (Ctrl/⌘ + K) — atteindre n'importe quel cabinet ou
 * praticien en trois frappes, sans quitter le clavier.
 *
 * Deux principes tenus :
 *  - AUCUNE donnée inventée : la recherche interroge les endpoints réels et
 *    n'affiche que ce que le serveur renvoie ;
 *  - jamais de rafale : la frappe est temporisée (200 ms) et une seule requête
 *    à la fois par nature de résultat, pour ne pas peser sur le budget Twenty.
 */
import { CabinetsListResponseSchema, PraticiensResponseSchema } from "@geceram/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../icons";

/** Combien de résultats de chaque nature — au-delà, on ouvre la liste. */
const PAR_NATURE = 6;

interface Resultat {
  cle: string;
  titre: string;
  detail: string;
  /** `null` = rien à ouvrir : la ligne existe dans Vevidental, pas dans le CRM. */
  vers: string | null;
  nature: "cabinet" | "praticien";
}

/**
 * Deux appels, deux contrats vérifiés.
 *
 * Ce qui a changé, et pourquoi c'est important :
 *
 * 1. LES LIENS. La palette construisait `/cabinets/${c.id}` et
 *    `/praticiens/${p.id}` avec l'identifiant de fiche Twenty. Depuis la
 *    bascule du 12/08, les deux listes portent les 1 253 cliniques et les
 *    1 120 docteurs de la copie Vevidental, et cet identifiant vaut `null`
 *    pour l'immense majorité d'entre eux : la palette naviguait vers
 *    `/cabinets/null`. Un praticien reste ouvrable par son identifiant
 *    Vevidental (la route accepte les deux ères) ; un cabinet sans fiche ne
 *    l'est pas — il s'affiche alors sans lien, et on DIT pourquoi.
 *
 * 2. LES ERREURS. Un 500 ou une coupure réseau devenaient `{ items: [] }`,
 *    donc « aucun résultat ne correspond ». Un échec de recherche affiché
 *    comme une absence de résultat est un mensonge : on remonte l'erreur.
 *
 * 3. LE CONTRAT. Les réponses étaient castées sans vérification. Elles
 *    passent par leur schéma Zod : une réponse d'une forme inattendue lève,
 *    au lieu de produire des liens silencieusement cassés.
 */
async function chercher(q: string, signal: AbortSignal): Promise<Resultat[]> {
  const commun = { credentials: "include" as const, signal };
  const lire = async <T,>(url: string, schema: { parse: (v: unknown) => T }): Promise<T> => {
    const r = await fetch(url, commun);
    if (!r.ok) throw new Error(`Le serveur a répondu ${String(r.status)}.`);
    return schema.parse(await r.json());
  };
  const [cabs, prats] = await Promise.all([
    lire(`/api/v1/cabinets?q=${encodeURIComponent(q)}`, CabinetsListResponseSchema),
    lire(`/api/v1/praticiens?q=${encodeURIComponent(q)}`, PraticiensResponseSchema),
  ]);

  const rc: Resultat[] = cabs.items.slice(0, PAR_NATURE).map((c) => ({
    cle: `cabinet-${String(c.idVevidental)}`,
    titre: c.nom,
    detail:
      [
        c.ville,
        c.travauxEnCours > 0 ? `${String(c.travauxEnCours)} en cours` : null,
        c.echeancesDepassees > 0 ? `${String(c.echeancesDepassees)} échéance(s) dépassée(s)` : null,
        c.id === null ? "sans fiche CRM — rien à ouvrir" : null,
      ]
        .filter(Boolean)
        .join(" · "),
    vers: c.id === null ? null : `/cabinets/${c.id}`,
    nature: "cabinet",
  }));
  const rp: Resultat[] = prats.items.slice(0, PAR_NATURE).map((p) => ({
    cle: `praticien-${String(p.idVevidental)}`,
    titre: p.nom,
    detail: [p.cabinetPrincipalNom, p.email].filter(Boolean).join(" · "),
    // La fiche praticien se construit entièrement sur la copie Vevidental :
    // elle s'ouvre même sans fiche Twenty.
    vers: `/praticiens/${String(p.idVevidental)}`,
    nature: "praticien",
  }));
  return [...rc, ...rp];
}

export function Palette() {
  const [ouvert, setOuvert] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState<Resultat[]>([]);
  const [echec, setEchec] = useState<string | null>(null);
  const [charge, setCharge] = useState(false);
  const [curseur, setCurseur] = useState(0);
  const champ = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const fermer = useCallback(() => {
    setOuvert(false);
    setQ("");
    setRes([]);
    setEchec(null);
    setCurseur(0);
  }, []);

  // Ctrl/⌘ + K ouvre depuis n'importe quel écran ; Échap ferme.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOuvert((v) => !v);
      } else if (e.key === "Escape") {
        setOuvert(false);
      }
    };
    window.addEventListener("keydown", onKey);
    const onOuvre = () => setOuvert(true);
    window.addEventListener("geceram:palette", onOuvre);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("geceram:palette", onOuvre);
    };
  }, []);

  useEffect(() => {
    if (ouvert) setTimeout(() => champ.current?.focus(), 30);
  }, [ouvert]);

  useEffect(() => {
    const terme = q.trim();
    if (terme.length < 2) {
      setRes([]);
      setEchec(null);
      return;
    }
    const ctrl = new AbortController();
    setCharge(true);
    const t = setTimeout(() => {
      void chercher(terme, ctrl.signal)
        .then((r) => {
          setRes(r);
          setEchec(null);
          setCurseur(0);
        })
        .catch((e: unknown) => {
          // Une recherche qui échoue n'est pas une recherche sans résultat.
          if (ctrl.signal.aborted) return;
          setRes([]);
          setEchec(e instanceof Error ? e.message : "La recherche a échoué.");
        })
        .finally(() => setCharge(false));
    }, 200);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  const aller = useCallback(
    (r: Resultat | undefined) => {
      // `vers === null` : la clinique existe dans Vevidental mais n'a pas de
      // fiche CRM. On ne navigue pas — l'ancienne palette envoyait alors sur
      // `/cabinets/null`, qui affichait une erreur d'ouverture de fiche.
      if (!r || r.vers === null) return;
      fermer();
      navigate(r.vers);
    },
    [fermer, navigate],
  );

  const vide = useMemo(
    () => q.trim().length >= 2 && !charge && res.length === 0 && echec === null,
    [q, charge, res, echec],
  );

  if (!ouvert) return null;
  return (
    <div
      role="presentation"
      onClick={fermer}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,.45)",
        zIndex: 60,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
      }}
    >
      <div
        role="dialog"
        aria-label="Recherche"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(640px, 92vw)",
          background: "var(--surface-card)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--r-card)",
          boxShadow: "0 24px 60px rgba(15,23,42,.28)",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--border-default)" }}>
          <Icon id="i-search" size="sm" />
          <input
            ref={champ}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setCurseur((c) => Math.min(c + 1, Math.max(0, res.length - 1)));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setCurseur((c) => Math.max(0, c - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                aller(res[curseur]);
              }
            }}
            placeholder="Cabinet, praticien, ville…"
            style={{
              flex: 1,
              border: 0,
              outline: "none",
              background: "transparent",
              color: "var(--text-primary)",
              fontSize: 15,
            }}
          />
          <kbd>Échap</kbd>
        </div>

        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
          {q.trim().length < 2 ? (
            <div style={{ padding: "18px 16px", fontSize: 13, color: "var(--text-muted)" }}>
              Tape au moins deux lettres. Flèches pour choisir, Entrée pour ouvrir.
            </div>
          ) : charge && res.length === 0 ? (
            <div style={{ padding: "18px 16px", fontSize: 13, color: "var(--text-muted)" }}>Recherche…</div>
          ) : echec ? (
            <div style={{ padding: "18px 16px", fontSize: 13, color: "var(--danger-600)" }} role="alert">
              La recherche n'a pas abouti — {echec} Ce n'est pas « aucun résultat » : réessaie dans un instant.
            </div>
          ) : vide ? (
            <div style={{ padding: "18px 16px", fontSize: 13, color: "var(--text-muted)" }}>
              Aucun cabinet ni praticien ne correspond à « {q.trim()} ».
            </div>
          ) : (
            res.map((r, i) => (
              <button
                key={r.cle}
                type="button"
                disabled={r.vers === null}
                title={r.vers === null ? "Cette clinique n'a pas encore de fiche dans le CRM" : undefined}
                onMouseEnter={() => setCurseur(i)}
                onClick={() => aller(r)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "11px 16px",
                  textAlign: "left",
                  background: i === curseur ? "var(--surface-subtle)" : "transparent",
                  border: 0,
                  borderBottom: "1px solid var(--border-default)",
                  cursor: r.vers === null ? "not-allowed" : "pointer",
                  opacity: r.vers === null ? 0.6 : 1,
                }}
              >
                <Icon id={r.nature === "cabinet" ? "i-building" : "i-users"} size="sm" />
                <span style={{ display: "grid", gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{r.titre}</span>
                  {r.detail ? (
                    <span style={{ fontSize: 12.5, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.detail}
                    </span>
                  ) : null}
                </span>
                <span className="chip sm" style={{ marginLeft: "auto" }}>
                  {r.nature === "cabinet" ? "Cabinet" : "Praticien"}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
